// FlightInterpolator.js - Smooth flight position interpolation
// Provides buttery-smooth 60fps plane movement across the map

// ============================================================================
// GREAT CIRCLE MATH
// ============================================================================

const DEG_TO_RAD = Math.PI / 180;
const RAD_TO_DEG = 180 / Math.PI;

/**
 * Calculate great circle intermediate point between two coordinates
 * This creates the realistic curved path that planes follow on a globe
 */
export function greatCircleInterpolate(lat1, lon1, lat2, lon2, fraction) {
  // Convert to radians
  const φ1 = lat1 * DEG_TO_RAD;
  const λ1 = lon1 * DEG_TO_RAD;
  const φ2 = lat2 * DEG_TO_RAD;
  const λ2 = lon2 * DEG_TO_RAD;

  // Calculate angular distance
  const Δφ = φ2 - φ1;
  const Δλ = λ2 - λ1;
  const a = Math.sin(Δφ / 2) ** 2 + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) ** 2;
  const δ = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  // For very short distances, use linear interpolation (avoids division issues)
  if (δ < 0.0001) {
    return {
      lat: lat1 + (lat2 - lat1) * fraction,
      lon: lon1 + (lon2 - lon1) * fraction,
    };
  }

  // Great circle interpolation
  const A = Math.sin((1 - fraction) * δ) / Math.sin(δ);
  const B = Math.sin(fraction * δ) / Math.sin(δ);

  const x = A * Math.cos(φ1) * Math.cos(λ1) + B * Math.cos(φ2) * Math.cos(λ2);
  const y = A * Math.cos(φ1) * Math.sin(λ1) + B * Math.cos(φ2) * Math.sin(λ2);
  const z = A * Math.sin(φ1) + B * Math.sin(φ2);

  const φ = Math.atan2(z, Math.sqrt(x * x + y * y));
  const λ = Math.atan2(y, x);

  return {
    lat: φ * RAD_TO_DEG,
    lon: λ * RAD_TO_DEG,
  };
}

/**
 * Calculate bearing between two points (0=north, 90=east)
 */
export function calculateBearing(lat1, lon1, lat2, lon2) {
  const φ1 = lat1 * DEG_TO_RAD;
  const φ2 = lat2 * DEG_TO_RAD;
  const Δλ = (lon2 - lon1) * DEG_TO_RAD;

  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);

  return (Math.atan2(y, x) * RAD_TO_DEG + 360) % 360;
}

/**
 * Smooth exponential interpolation for position updates
 * This creates the "easing" effect so planes don't snap
 */
export function smoothLerp(current, target, smoothing, deltaTime) {
  // Exponential smoothing factor (higher = slower)
  const factor = 1 - Math.pow(smoothing, deltaTime / 16.67); // Normalized to 60fps
  return current + (target - current) * factor;
}

/**
 * Normalize angle difference for smooth rotation
 */
export function normalizeAngleDiff(current, target) {
  let diff = target - current;
  while (diff > 180) diff -= 360;
  while (diff < -180) diff += 360;
  return diff;
}

/**
 * Smooth bearing interpolation (handles 360->0 wrap)
 */
export function smoothBearing(current, target, smoothing, deltaTime) {
  const diff = normalizeAngleDiff(current, target);
  const factor = 1 - Math.pow(smoothing, deltaTime / 16.67);
  return (current + diff * factor + 360) % 360;
}

// ============================================================================
// FLIGHT STATE INTERPOLATOR
// ============================================================================

/**
 * Create an interpolated flight state manager
 * Tracks target positions from game state and smoothly animates toward them
 */
export function createFlightInterpolator() {
  // Internal state: maps flight ID to current rendered position
  const renderedPositions = new Map();

  // Smoothing factors (0-1, higher = snappier)
  // These are "per-frame catch-up" values, time-normalized elsewhere
  const POSITION_SMOOTHING = 0.18; // How fast position catches up (0.15-0.20 typical)
  const BEARING_SMOOTHING = 0.15;  // Rotation slightly slower than position (feels natural)

  return {
    /**
     * Update all flight positions with smooth interpolation
     * @param {Array} targetFlights - Flights with target positions from game state
     * @param {number} deltaTime - Time since last frame in ms
     * @returns {Array} - Flights with smoothly interpolated positions
     */
    update(targetFlights, deltaTime) {
      const smoothedFlights = [];
      const activeIds = new Set();

      for (const flight of targetFlights) {
        activeIds.add(flight.id);

        // Get or initialize rendered state for this flight
        let rendered = renderedPositions.get(flight.id);

        if (!rendered) {
          // First time seeing this flight - initialize at target position
          rendered = {
            lat: flight.currentLat,
            lon: flight.currentLon,
            bearing: flight.bearing || 0,
            progress: flight.overallProgress || 0,
          };
          renderedPositions.set(flight.id, rendered);
        }

        // Target position from game state
        const targetLat = flight.currentLat;
        const targetLon = flight.currentLon;
        const targetBearing = flight.bearing || 0;
        const targetProgress = flight.overallProgress || 0;

        // Smooth interpolation toward target
        // Use great circle for the actual path
        const progressDiff = Math.abs(targetProgress - rendered.progress);

        if (progressDiff > 0.001) {
          // Calculate intermediate point on great circle path
          const origin = { lat: flight.originLat, lon: flight.originLon };
          const dest = { lat: flight.destLat, lon: flight.destLon };

          // Smoothly interpolate the progress value
          rendered.progress = smoothLerp(rendered.progress, targetProgress, POSITION_SMOOTHING, deltaTime);

          // Get position on great circle at interpolated progress
          const pos = greatCircleInterpolate(
            origin.lat, origin.lon,
            dest.lat, dest.lon,
            rendered.progress
          );

          rendered.lat = pos.lat;
          rendered.lon = pos.lon;
        } else {
          // Close enough - just smooth directly to target
          rendered.lat = smoothLerp(rendered.lat, targetLat, POSITION_SMOOTHING, deltaTime);
          rendered.lon = smoothLerp(rendered.lon, targetLon, POSITION_SMOOTHING, deltaTime);
          rendered.progress = targetProgress;
        }

        // Smooth bearing rotation
        rendered.bearing = smoothBearing(rendered.bearing, targetBearing, BEARING_SMOOTHING, deltaTime);

        // Build smoothed flight object
        smoothedFlights.push({
          ...flight,
          // Override position with smoothed values
          currentLat: rendered.lat,
          currentLon: rendered.lon,
          bearing: rendered.bearing,
          // Keep original for calculations
          _targetLat: targetLat,
          _targetLon: targetLon,
          _targetBearing: targetBearing,
        });
      }

      // Clean up flights that no longer exist
      for (const id of renderedPositions.keys()) {
        if (!activeIds.has(id)) {
          renderedPositions.delete(id);
        }
      }

      return smoothedFlights;
    },

    /**
     * Reset a specific flight's position (e.g., on phase change)
     */
    reset(flightId) {
      renderedPositions.delete(flightId);
    },

    /**
     * Clear all tracked positions
     */
    clear() {
      renderedPositions.clear();
    },
  };
}

// ============================================================================
// SINGLETON INTERPOLATOR
// ============================================================================

let globalInterpolator = null;

export function getFlightInterpolator() {
  if (!globalInterpolator) {
    globalInterpolator = createFlightInterpolator();
  }
  return globalInterpolator;
}

export default {
  greatCircleInterpolate,
  calculateBearing,
  smoothLerp,
  smoothBearing,
  createFlightInterpolator,
  getFlightInterpolator,
};
